export const matchFieldPermission = (fieldId, fieldPermissions, layerId) => {
    const permission = fieldPermissions.find(
        (f) => f.layerId === layerId && f.fieldId === fieldId
    );
    return permission;
};

export const filterReadLayers = (vectorLayers, layerPermissions) => {
    const newVectors = vectorLayers.filter((v) => {
        const matchedPerm = layerPermissions.find((p) => p.layerId === v.Id);
        if (matchedPerm === undefined) return true;
        return matchedPerm.permissions.Read;
    });
    return newVectors;
};

export const filterReadFields = (fieldsArray, fieldPermissions, layerId) => {
    const newFields = fieldsArray.filter((d) => {
        const permission = matchFieldPermission(d.Name, fieldPermissions, layerId);
        if (permission === undefined) return true;
        return permission.permissions.Read;
    });
    return newFields;
};

export const updateEditFields = (fields, fieldPermissions, vector) => {
    const returnArr = [];
    if (vector.Editable) {
        // Check each field to see if it is editable
        fields.forEach((f) => {
            if (f.Editable === false) {
                const newObj = Object.assign({}, { ...f, Editable: false });
                returnArr.push(newObj);
            }
            // Match permissions
            else {
                const fieldPerm = matchFieldPermission(
                    f.Name,
                    fieldPermissions,
                    vector.Id
                );
                let edit;
                // If no specified permission assume false
                if (fieldPerm === undefined) {
                    edit = false;
                } else if (fieldPerm.permissions.EditAttributes === true) {
                    edit = true;
                } else {
                    edit = false;
                }
                const newObj = Object.assign({}, { ...f, Editable: edit });
                returnArr.push(newObj);
            }
        });
    } else {
        fields.forEach((f) => {
            const newObj = Object.assign({}, { ...f, Editable: false });
            returnArr.push(newObj);
        });
    }
    return returnArr;
};

/**
 * Takes in parent permissions and authorized permissions for group or layer
 *  and combines them into new specific permissions
 * Will only restrict permissions or permit IF permitting a permission that is false by default and has not
 *  already been specified as false by a parent
 * @param {ConfigLayerPermission[] | []} authPermissions Array of permissions from field/group/layer that is filtered by user roles
 * @param {LayerPermission | {}} parentPermissions Permissions from parent (will be empty object if no parentPermissions)
 * @returns {LayerPermission}
 */
export const mergePermissions = (authPermissions, parentPermissions) => {
    const childPermissions = { ...parentPermissions };
    authPermissions.forEach((p) => {
        if (Object.keys(childPermissions).length === 0) {
            Object.assign(childPermissions, p);
        } else {
            for (const key in p) {
                // if value is false update childPermissions
                if (key in childPermissions && p[key] === false) {
                    childPermissions[key] = false;
                }
                // if value is not in childPermissions merge value
                else if (!key in childPermissions) {
                    childPermissions[key] = p[key];
                }
            }
        }
        // Assign defaults for permissions not specified
        if ("Read" in childPermissions === false) childPermissions.Read = true;
        if ("CreateFeature" in childPermissions === false)
            childPermissions.CreateFeature = false;
        if ("UpdateFeature" in childPermissions === false)
            childPermissions.UpdateFeature = false;
        if ("DeleteFeature" in childPermissions === false)
            childPermissions.DeleteFeature = false;
        if ("GeometryEditable" in childPermissions === false)
            childPermissions.GeometryEditable = false;
        if (!"EditAttachment" in childPermissions === false)
            childPermissions.EditAttachment = false;
        // Remove unneeded fields
        if (childPermissions.Value) delete childPermissions.Value;
        if (childPermissions.Type) delete childPermissions.Type;
    });
    return childPermissions;
};

/**
 * Translates layer permission to field and combines with given field permissions
 * @param {ConfigFieldPermission[] | undefined} fieldPermissions
 * @param {LayerPermission} layerPermissions
 * @returns {FieldPermission}
 */
const mergeLayerWithFieldPermissions = (fieldPermissions, layerPermissions) => {
    // Get values for Read, UpdateFeature and CreateFeature from layerPermissions
    // If UpdateFeature or CreateFeature are true then EditAttributes is true
    const inheritableFromLayer = {
        EditAttributes:
            (layerPermissions?.UpdateFeature || layerPermissions?.CreateFeature) ?? false,
        Read: layerPermissions?.Read ?? true,
    };
    if (!fieldPermissions?.length) {
        return inheritableFromLayer;
    }
    const givenFieldPermissions = reduceFieldPermissions([
        ...fieldPermissions,
        inheritableFromLayer,
    ]);
    switch (true) {
        // Handle when read is false first
        case inheritableFromLayer.Read === false || givenFieldPermissions.Read === false:
            return { EditAttributes: false, Read: false };
        // Check if layer is restricted from editing; cascade permission downward
        case inheritableFromLayer.EditAttributes === false:
            return { EditAttributes: false, Read: true };
        // Check if field is restricted from editing
        case givenFieldPermissions.EditAttributes === false:
            return { EditAttributes: false, Read: true };
        // If neither edit attributes or read is restricted for layer or field
        case givenFieldPermissions.EditAttributes === true:
            return { EditAttributes: true, Read: true };
        default:
            console.log("Field permissions edge case missed");
    }
};

class PermissionHelper {
    constructor(permissions, authedRoles) {
        this.permissions = permissions;
        this.roleArr = authedRoles.map((a) => a.Value);
    }
    /**
     * @param {HelperPermissionOutput} permissions
     * @returns { false | HelperPermissionOutput}
     */
    authorizePermissions(permissions) {
        if (!permissions) return false;
        const auth = permissions.permissions.filter((p) =>
            this.roleArr.includes(p.Value)
        );
        if (auth.length > 0) {
            return auth;
        } else return false;
    }
    /**
     * @param {GroupSpecificConfigPermission} group
     */
    authorizeGroupPermissions(group) {
        const p = this.permissions.group.find((p) => p.groupId === group.Name);
        return this.authorizePermissions(p);
    }
    /**
     * @param {LayerSpecificConfigPermission} layer
     */
    authorizeLayerPermissions(layer) {
        const p = this.permissions.layer.find((p) => p.layerId === layer.Id);
        return this.authorizePermissions(p);
    }
    /**
     * @param {FieldSpecificConfigPermission} field
     * @param {string} layerId
     */
    authorizeFieldPermissions(field, layerId) {
        const p = this.permissions.field.find(
            (p) => p.fieldId === field.Name && p.layerId === layerId
        );
        return this.authorizePermissions(p);
    }
}

/**
 * Iterate over layer tree and create permissions for groups, layers and fields based on roles
 * @param {RoleObject[]} authedRoles Authorized roles base on user
 * @param {object[]} layers Layer tree from redux
 * @param {object} permissions Permissions from redux config data
 * @returns {
 *      [
 *      MergedGroupPermission | [],
 *      MergedLayerPermissionLayerPermissions | [],
 *      MergedFieldPermission | []
 *      ]
 *  }
 */
export const cascadePermissions = (authedRoles, layers, permissions) => {
    const Helper = new PermissionHelper(permissions, authedRoles);
    const mergedGroupPermissions = [];
    const mergedLayerPermissions = [];
    const mergedFieldPermissions = [];
    layers.forEach((l) => {
        // handle ungrouped geojson layers
        const layerPermissions = Helper.authorizeLayerPermissions(l);
        if (layerPermissions) {
            const mergedPermissions = mergePermissions(layerPermissions, {});
            mergedLayerPermissions.push({
                layerId: l.Id,
                permissions: mergedPermissions,
            });
            return;
        }
        const groupAuthPermissions = Helper.authorizeGroupPermissions(l);
        // If there is not group level permissions, get layer level permissions
        if (l.SubLayers !== undefined && !groupAuthPermissions) {
            makeArray(l.SubLayers).forEach((layer) => {
                const layerPermissions = Helper.authorizeLayerPermissions(layer);
                if (layerPermissions) {
                    const mergedPermissions = mergePermissions(layerPermissions, {});
                    mergedLayerPermissions.push({
                        layerId: layer.Id,
                        permissions: mergedPermissions,
                    });
                }
            });
        } else if (groupAuthPermissions.length > 0) {
            // Make object of permissions to cascade
            const newRefObj = mergePermissions(groupAuthPermissions, {});
            mergedGroupPermissions.push({
                groupId: l.Name,
                permissions: newRefObj,
            });
        } else {
            // do nothing, there are no permissions at first level of layer tree
        }
    });

    // Create permissions array for layer
    mergedGroupPermissions.forEach((perm) => {
        const group = layers.find((l) => l.Name === perm.groupId);
        // find permissions for layer
        makeArray(group.SubLayers).forEach((l) => {
            const layerAuthPermissions = Helper.authorizeLayerPermissions(l);
            // Assign permissions from parent to layer
            if (!layerAuthPermissions) {
                mergedLayerPermissions.push({
                    layerId: l.Id,
                    permissions: perm.permissions,
                });
            }
            // Combine layer with parent permissions
            else {
                const newRefObj = mergePermissions(
                    layerAuthPermissions,
                    perm.permissions
                );
                mergedLayerPermissions.push({
                    layerId: l.Id,
                    permissions: newRefObj,
                });
            }
        });
    });

    // Create field permissions
    mergedLayerPermissions.forEach((perm) => {
        // Match permission to layers from state (configData.layers)
        const layer = layersSearch(layers, perm.layerId);
        if (layer?.Fields === undefined) return;
        const fields = makeArray(layer.Fields.Field);
        fields.forEach((f) => {
            const authPermissions = Helper.authorizeFieldPermissions(f, perm.layerId);
            const newPermissions = mergeLayerWithFieldPermissions(
                authPermissions,
                perm.permissions
            );
            mergedFieldPermissions.push({
                layerId: perm.layerId,
                fieldId: f.Name,
                permissions: newPermissions,
            });
        });
    });
    // Add any field permissions for field with permissions that did not have permissions cascaded down
    permissions.field.forEach((f) => {
        if (!f) return;
        // Check if permission already exists
        const alreadyExists = mergedFieldPermissions.find(
            (p) => p.layerId === f.layerId && p.fieldId === f.fieldId
        );
        // Return since permission has already been added
        if (alreadyExists) {
            return;
        }
        const fieldAuthPermissions = Helper.authorizePermissions(f);
        // No authorized permissions, do not create permission for field, defualt will be assumed
        if (!fieldAuthPermissions) {
            return;
        }
        // Get field permissions
        const reducedPermissions = reduceFieldPermissions(fieldAuthPermissions);
        mergedFieldPermissions.push({
            layerId: f.layerId,
            fieldId: f.fieldId,
            permissions: reducedPermissions,
        });
    });
    return [mergedGroupPermissions, mergedLayerPermissions, mergedFieldPermissions];
};

/**
 * Takes in one to multiple permissions and reduces them to one
 * Restricted approach taken - any falses for a FieldPermission will result in the property being false
 * @param {ConfigFieldPermission[]} permissions All permissions for field from config
 * @returns {FieldPermission}
 */
const reduceFieldPermissions = (permissions) => {
    // In case of multiple roles and permissions
    const permissionsAccumulator = {};
    permissions?.forEach((p, i) => {
        if (i === 0) {
            Object.assign(permissionsAccumulator, p);
        }
        if (p?.Read === false) {
            permissionsAccumulator["Read"] = false;
        }
        if (p?.EditAttributes === false) {
            permissionsAccumulator["EditAttributes"] = false;
        }
    });
    // Get values for EditAttributes and Read from field permissions
    // Assign defaults if undefined
    const givenFieldPermissions = {
        Read: permissionsAccumulator?.Read ?? true,
        EditAttributes: permissionsAccumulator?.EditAttributes ?? false,
    };
    return givenFieldPermissions;
};

// takes array or object and returns array
export const makeArray = (inData) => {
    return Array.isArray(inData) ? inData : [inData];
};

// Take in (configData.layers) and layer.Id and return matched layer
// -breadth first search-
export const layersSearch = (layers, layerId) => {
    let layer;
    layer = layers.find((l) => l.Id === layerId);
    if (layer === undefined) {
        layers.forEach((l) => {
            if (l.SubLayers !== undefined) {
                makeArray(l.SubLayers).forEach((s) => {
                    if (s.Id === layerId) {
                        layer = s;
                    }
                    if (layer !== undefined) return;
                });
            }
            if (layer !== undefined) return;
        });
    }
    return layer;
};

/**
 * Definition of permission for a field
 * @typedef {Object} FieldPermission
 * @property {boolean} Read Indicates whether field can be read or not
 * @property {boolean} EditAttributes Indicates whether field can be edited or not
 */

/**
 * Definition of permission given from configuration file for field
 * So far Type is always "Role" and Value is always the name of the role
 * @typedef {Object} ConfigFieldPermission
 * @property {boolean} [Read] Indicates whether field can be read or not
 * @property {boolean} [EditAttributes] Indicates whether field can be edited or not
 * @property {"Role" | string} [Type] Indicated that permission is a role unless specified otherwise
 * @property {string} [Value] Indicated the value of the role
 */

/**
 * @typedef {Object} LayerPermission
 * @property {boolean} Read Allows or restricts the user from reading viewing the layer
 * @property {boolean} CreateFeature Allows or restricts the user from creating new features
 * @property {boolean} UpdateFeature Allows or restricts the user from updating features
 * @property {boolean} DeleteFeature Allows or restricts the user from deleting features
 * @property {boolean} GeometryEditable Allows or restricts the user from editing the geometry of a feature
 * @property {boolean} EditAttachment
 */

/**
 * @typedef {Object} ConfigLayerPermission
 * @property {boolean} [Read] Allows or restricts the user from reading viewing the layer
 * @property {boolean} [CreateFeature] Allows or restricts the user from creating new features
 * @property {boolean} [UpdateFeature] Allows or restricts the user from updating features
 * @property {boolean} [DeleteFeature] Allows or restricts the user from deleting features
 * @property {boolean} [GeometryEditable] Allows or restricts the user from editing the geometry of a feature
 * @property {boolean} [EditAttachment]
 * @property {"Role" | string} Type Indicated that permission is a role unless specified otherwise
 * @property {string} Value Indicated the value of the role
 */

/**
 * @typedef {Object} RoleObject
 * @property {string} Value Role name
 */

/**
 * @typedef {Object} MergedLayerPermission
 * @property {string} layerId Id of layer
 * @property {LayerPermission} permissions
 */

/**
 * @typedef {Object} MergedFieldPermission
 * @property {string} layerId
 * @property {string} fieldId Corresponds to Name attribute from field
 * @property {FieldPermission} permissions
 */

/**
 * @typedef {Object} MergedGroupPermission
 * @property {string} groupId Corresponds to Name attribute from group
 * @property {LayerPermission} permissions
 */

/**
 * @typedef {Object} GroupSpecificConfigPermission
 * @property {string} groupId Corresponds to Name attribute from group
 * @property {ConfigLayerPermission[]} permissions
 */

/**
 * @typedef {Object} LayerSpecificConfigPermission
 * @property {string} layerId Id of layer
 * @property {ConfigLayerPermission[]} permissions
 */

/**
 * @typedef {Object} FieldSpecificConfigPermission
 * @property {string} layerId
 * @property {string} fieldId Corresponds to Name attribute from field
 * @property {ConfigFieldPermission[]} permissions
 */

/**
 * @typedef {Object} HelperPermissionOutput
 * @property {LayerSpecificConfigPermission | GroupSpecificConfigPermission | FieldSpecificConfigPermission} permissions
 */
